<script lang="ts">
  import { photoUrl } from './api';
  import { findTodayHoursLine } from './opening-hours';
  import { formatTypeLabel } from '~/shared/place-type.js';
  import { routeActionLabel } from '~/shared/route-handoff.js';
  import type { PoiView } from '~/shared/api.js';

  let {
    poi,
    routeEmpty = true,
    inRoute = false,
    hoursLocale = null,
    onClose,
    onDetails,
    onAddToRoute,
  }: {
    poi: PoiView;
    routeEmpty?: boolean;
    inRoute?: boolean;
    hoursLocale?: string | null;
    onClose: () => void;
    onDetails: () => void;
    onAddToRoute?: (poi: PoiView) => void;
  } = $props();

  const displayName = $derived(poi.customName ?? poi.name);
  const typeLabel = $derived(formatTypeLabel(poi.category));
  const ratingLabel = $derived(poi.rating !== null ? poi.rating.toFixed(1) : null);
  const thumb = $derived(photoUrl(poi.photoReference, 240));
  const trimmedNotes = $derived(poi.notes && poi.notes.trim() ? poi.notes.trim() : null);
  const routeButtonLabel = $derived(routeActionLabel({ inRoute, routeEmpty }));
  // Today's hours line only — the popup is too small for the full
  // week. Users tap Details to see the rest.
  const todayHoursLine = $derived(
    poi.openingHours ? findTodayHoursLine(poi.openingHours, hoursLocale) : null,
  );
</script>

<div class="map-popup">
  <div class="close" role="button" tabindex="0" aria-label="Close" onclick={onClose} onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}>
    <!-- Plain Unicode × character (U+00D7) rendered as text. CSS
         pseudo-elements and inline SVG both rendered in DevTools but
         not on screen inside Google's mounted InfoWindow content
         (likely a currentColor / SVG-stroke override from .gm-style).
         A text glyph has nothing to override — it's just a character
         in the font using the element's `color`. Inline styles on the
         span force size and weight so no global rule can flatten it. -->
    <span aria-hidden="true" style="font-size: 22px; line-height: 1; font-weight: 600; color: inherit;">×</span>
  </div>
  <div class="head">
    {#if thumb}
      <div class="thumb"><img src={thumb} alt="" /></div>
    {:else}
      <div class="thumb thumb-blank"></div>
    {/if}
    <div class="head-text">
      <h3>{displayName}</h3>
      <div class="meta">
        {#if ratingLabel}
          <span class="rating">★ {ratingLabel}</span>
        {/if}
        {#if ratingLabel && typeLabel}<span aria-hidden="true">·</span>{/if}
        {#if typeLabel}
          <span>{typeLabel}</span>
        {/if}
      </div>
    </div>
  </div>
  {#if todayHoursLine}
    <p class="hours-line">{todayHoursLine}</p>
  {/if}
  {#if trimmedNotes}
    <p class="notes">{trimmedNotes}</p>
  {/if}
  <div class="actions">
    {#if onAddToRoute}
      <button type="button" disabled={inRoute} onclick={() => onAddToRoute(poi)}>
        {routeButtonLabel}
      </button>
    {/if}
    <button type="button" onclick={onDetails}>Details</button>
  </div>
</div>
